import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import DashboardCards from './DashboardCards';
import { PreloadedQuery, useLazyLoadQuery } from 'react-relay';
import { DashboardSkillsByTargetQuery } from '../../graphQLOperations/__generated__/DashboardSkillsByTargetQuery.graphql';
import { StudentDashboardAssignments } from './StudentDashboardAssignments';
import { DashboardGetStudentExitSlipQuery } from '../../graphQLOperations/__generated__/DashboardGetStudentExitSlipQuery.graphql';
import { selectedStudentSkillStates } from '../../redux/reducers/studentSkillsSlice';
import { selectedUnit } from '../../redux/reducers/unitSlice';
import { getPracticeProblemsActions, getSkillAncestries } from '../../graphQLOperations/Dashboard';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { DashboardSkillAncestriesQuery } from '../../graphQLOperations/__generated__/DashboardSkillAncestriesQuery.graphql';
import { getSkillsLevelByUnit, SkillGraphNode } from '../../businessLogicService/getSkillsLevelByUnit';
import { getSkillsInfoByLevel } from '../../businessLogicService/getSkillsInfoByLevel';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { getFoundations } from '../../businessLogicService/StudentDashboardAux/getFoundations';
import { orderTargetPaths } from '../../businessLogicService/StudentDashboardAux/orderTargetPaths';
import { PathSkillNode } from '../../businessLogicService/StudentDashboardAux/pathToSkills';
import { DashboardUnitHeader } from './DashboardUnitHeader';
import { StickyHeaderOnScroll } from './StickyHeaderOnScroll';
import { UnitPathSegmentProvider } from '../../contexts/UnitPathSegmentContext';
import { skillSelector } from '../../businessLogicService/StudentDashboardAux/foundationSkills';
import { applyOptimisticValues } from '../../businessLogicService/StudentDashboardAux/applyOptimisticValues';
import { selectedStudentSkillStateAfterTakeAssessment } from '../../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { DashboardPracticeProblemsActionsQuery } from '../../graphQLOperations/__generated__/DashboardPracticeProblemsActionsQuery.graphql';
import { selectUser } from '../../redux/reducers/userSlice';

export interface FoundationSkills extends SkillGraphNode {
  id: number;
  difficulty: number | undefined;
  name: string;
  code: string;
  unitImpact: number | undefined;
  readiness?: number;
}

interface Props {
  customCardMessage: string;
  queryReferenceSkillsByTarget: PreloadedQuery<DashboardSkillsByTargetQuery>;
  queryReferenceExitSlip: PreloadedQuery<DashboardGetStudentExitSlipQuery>;
}

const DashboardContainerBody: FC<Props> = ({ queryReferenceExitSlip }) => {
  const userRedux = useAppSelector(selectUser);
  const [foundationSkills, setFoundationSkills] = useState<FoundationSkills[] | null>(null);
  const [pathToSkills, setPathToSkills] = useState<PathSkillNode[][][]>();
  const unitRedux = useAppSelector(selectedUnit);
  const optimisticValues = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);
  const studentSkills = useAppSelector(selectedStudentSkillStates);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const [, /*skillIdForRefresherCard*/ setSkillIdForRefresherCard] = useState<string | null>(null);
  /*const [queryReferenceRefresher, loadRefresherQuery] =
    useQueryLoader<DashboardGetStudentSkillStateBySkillIdQuery>(getStudentSkillStateBySkillId);
  const [queryReferenceRefresherTargetSkills, loadRefresherQueryTargetSkills] =
    useQueryLoader<DashboardSkillsAndTargetsByTargetGroupQuery>(skillsAndTargetsByTargetGroup);
*/
  const { skillAncestries } = useLazyLoadQuery<DashboardSkillAncestriesQuery>(getSkillAncestries, {
    filter: {
      skillIds: studentSkills?.map(sk => sk.skill.id) || [],
      onlyGreaterThanZeroWeight: true,
      ancestorSkillIds: studentSkills?.map(sk => sk.skill.id) || [],
    },
    skip: !studentSkills?.length,
  });

  const { learnosityPracticeProblemsSessions } = useLazyLoadQuery<DashboardPracticeProblemsActionsQuery>(
    getPracticeProblemsActions,
    {
      input: {
        studentIds: [`StudentNode:${userRedux?.id}`],
        skillIds: studentSkills?.map(sk => sk.skill.id),
        isActive: 'ACTIVE',
      },
    },
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    //map skill ancestries to flag skills as target

    const ancestriesWithTypeAndUnits = skillAncestries?.map(sa => {
      const stdSkill = studentSkills?.find(sk => sk.skill.id === `SkillNode:${sa.baseSkillId}`);
      return {
        ...sa,
        isTarget: stdSkill?.targetSkillType === TargetSkillTypeLabel.TARGET,
      };
    });

    const basicGraphOrder = getSkillsLevelByUnit(ancestriesWithTypeAndUnits);
    const skillInfo = studentSkills?.map(stsk => {
      const skill = stsk.skill;
      return {
        id: Number(splitId(skill.id)),
        difficulty: skill.difficulty,
        name: skill.name,
        code: skill.code,
        unitImpact: skill.targetSkills?.filter(ts => ts.targetSkillType.name === TargetSkillTypeLabel.SUPPORT).length,
        readiness: stsk.readinessScore,
      };
    });

    const skillsInfoByLevel = getSkillsInfoByLevel(basicGraphOrder, skillInfo ?? []);
    const foundationSkills = getFoundations(skillsInfoByLevel, targetGroupRedux?.targets?.length);
    const lowRelevanceUnitSkills = skillInfo?.filter(
      skill =>
        !skillsInfoByLevel
          .flat(2)
          .map(sk => sk.id)
          .includes(skill.id),
    );
    const foundations = lowRelevanceUnitSkills?.length
      ? [...(lowRelevanceUnitSkills as FoundationSkills[]), ...foundationSkills]
      : foundationSkills;

    setFoundationSkills(foundations);
    setPathToSkills(orderTargetPaths(skillsInfoByLevel, skillAncestries));

    /*console.log('Low relevance foundation skills', lowRelevanceUnitSkills);
    console.log('Foundation skills', foundationSkills);
    console.log('Paths to skills', skillsInfoByLevel);*/
  }, [skillAncestries, studentSkills]);

  /*useEffect(() => {
    if (targetGroupRedux) loadRefresherQueryTargetSkills({ targetGroupId: targetGroupRedux?.id });
  }, [targetGroupRedux]);*/

  /*useEffect(() => {
    if (skillIdForRefresherCard) {
      loadRefresherQuery({ skillIds: [skillIdForRefresherCard], studentId: `StudentNode:${userRedux?.id as string}` });
    }
  }, [skillIdForRefresherCard]);*/

  return (
    <div className="w-full lg:flex">
      <div className={`flex flex-column align-items-center justify-content-start tw-w-full p-component `}>
        {/* One scroll-spy for the unit header and the path's floating rail, so they always name the
            same segment. */}
        <UnitPathSegmentProvider>
          <div className="tw-w-full flex flex-column gap-lg">
            <StudentDashboardAssignments />
            {unitRedux && (
              <StickyHeaderOnScroll>
                <DashboardUnitHeader
                  foundationSkills={skillSelector(
                    foundationSkills || [],
                    applyOptimisticValues(studentSkills || [], optimisticValues.nextState),
                  )}
                  pathToSkills={skillSelector(
                    pathToSkills?.flat(2) || [],
                    applyOptimisticValues(studentSkills || [], optimisticValues.nextState),
                  )}
                />
              </StickyHeaderOnScroll>
            )}

            <DashboardCards
              foundationsSkills={foundationSkills}
              pathsToSkills={pathToSkills}
              queryReferenceExitSlip={queryReferenceExitSlip as PreloadedQuery<DashboardGetStudentExitSlipQuery>}
              setSkillIdForRefresherCard={setSkillIdForRefresherCard}
              lessonPractice={learnosityPracticeProblemsSessions}
            />
          </div>
        </UnitPathSegmentProvider>
      </div>
    </div>
  );
};

export default DashboardContainerBody;
